import { Routes, Route } from 'react-router-dom';
import { useGetMeQuery } from './store/apiSlice.js';
import Layout from './components/Layout.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import RequireAuth from './components/RequireAuth.jsx';
import RequireModule from './components/RequireModule.jsx';

import LoginPage from './pages/LoginPage.jsx';
import RegisterPage from './pages/RegisterPage.jsx';
import AccountPage from './pages/AccountPage.jsx';
import DashboardPage from './pages/DashboardPage.jsx';
import CatalogPage from './pages/CatalogPage.jsx';
import VendorsPage from './pages/VendorsPage.jsx';
import CustomersPage from './pages/CustomersPage.jsx';
import StockInPage from './pages/StockInPage.jsx';
import StockInInvoicePage from './pages/StockInInvoicePage.jsx';
import StockOutPage from './pages/StockOutPage.jsx';
import InvoicePage from './pages/InvoicePage.jsx';
import PricesPage from './pages/PricesPage.jsx';
import StockStatusReportPage from './pages/StockStatusReportPage.jsx';
import SalesReportPage from './pages/SalesReportPage.jsx';
import UsersPage from './pages/UsersPage.jsx';
import NotFoundPage from './pages/NotFoundPage.jsx';

/** `key` forces a fresh CatalogPage when moving between the four catalogs. */
const catalogs = [
  {
    path: '/products',
    kind: 'productName',
    module: 'Product Name',
    eyebrow: 'Catalog',
    title: 'Product Name',
    label: 'Product Name',
    subtitle: 'Manage product types — Weft, Clip On Extension, Remy Hair, I Tip, U Tip and so on.',
    placeholder: 'e.g. Weft / I Tip / U Tip / Remy Hair',
  },
  {
    path: '/textures',
    kind: 'texture',
    module: 'Texture Management',
    eyebrow: 'Catalog',
    title: 'Texture Management',
    label: 'Texture',
    subtitle: 'Manage the textures used across Stock In, Stock Out and Pricing.',
    placeholder: 'e.g. Straight / Curly / Wavy',
  },
  {
    path: '/lengths',
    kind: 'length',
    module: 'Length Management',
    eyebrow: 'Catalog',
    title: 'Length Management',
    label: 'Length',
    subtitle: 'Manage the lengths used across Stock In, Stock Out and Pricing.',
    placeholder: 'e.g. 18 in / 20 in / 22 in',
  },
  {
    path: '/colors',
    kind: 'color',
    module: 'Color Management',
    eyebrow: 'Catalog',
    title: 'Color Management',
    label: 'Color',
    subtitle: 'Manage the colors used across Stock In, Stock Out and Pricing.',
    placeholder: 'e.g. Natural Black / Brown / Blonde',
  },
];

/** Every page is wrapped the same way: crash guard outside, permission inside. */
function guarded(moduleName, element) {
  return <ErrorBoundary>{moduleName ? <RequireModule module={moduleName}>{element}</RequireModule> : element}</ErrorBoundary>;
}

export default function App() {
  // Ask once, at the root, who is signed in. A 401 is the normal answer for a
  // signed-out visitor; `authSlice` records it and flips `booted`. Running this
  // here rather than inside RequireAuth means /login can redirect someone who
  // still holds a valid cookie, instead of showing them the form again.
  useGetMeQuery();

  return (
    <Routes>
      {/* Public. Reachable with no session. */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />

      {/* Everything else needs a session; RequireAuth bounces to /login. */}
      <Route element={<RequireAuth />}>
        <Route element={<Layout />}>
          <Route index element={guarded(null, <DashboardPage />)} />

          {catalogs.map(({ path, module: moduleName, ...props }) => (
            <Route
              key={path}
              path={path}
              element={
                <ErrorBoundary key={props.kind}>
                  <RequireModule module={moduleName}>
                    <CatalogPage key={props.kind} {...props} />
                  </RequireModule>
                </ErrorBoundary>
              }
            />
          ))}

          <Route path="/vendors" element={guarded('Vendor Management', <VendorsPage />)} />
          <Route path="/customers" element={guarded('Customer Management', <CustomersPage />)} />
          <Route path="/stock-in" element={guarded('Stock In Management', <StockInPage />)} />
          <Route path="/stock-in/invoice/:id" element={guarded('Stock In Management', <StockInInvoicePage />)} />
          <Route path="/stock-out" element={guarded('Stock Out / Invoice', <StockOutPage />)} />
          <Route path="/invoice/:id" element={guarded('Stock Out / Invoice', <InvoicePage />)} />
          <Route path="/prices" element={guarded('Price Management', <PricesPage />)} />
          <Route path="/reports/stock-status" element={guarded('Reports', <StockStatusReportPage />)} />
          <Route path="/reports/sales" element={guarded('Reports', <SalesReportPage />)} />
          <Route path="/users" element={guarded('User Management', <UsersPage />)} />
          <Route path="/account" element={guarded(null, <AccountPage />)} />

          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Route>
    </Routes>
  );
}
